import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { PaymentPackage, PaymentPackageRecipient, PaymentTransmittal, RecipientType } from './transmittal.models';
import { filter, forEach, map, reject, remove, some, sortBy } from 'lodash';
import { TransmittalService } from './transmittal.service';
import { MessageBoxService } from 'src/app/UI-Lib/Message-Box/messagebox.service.upgrade';
import {
    ContactListModalComponent,
    ContactListModalParams,
} from 'src/app/Contact/Panel/List/Associate-Contact/contactListModal.component';
import { WeissmanModalService } from 'src/app/Compliance/WeissmanModalService';
import { EntityTypeIds } from 'src/app/constants.new';
import { ToastrService } from 'ngx-toastr';

export interface ShowDetailOutput {
    selectedTransmittal: PaymentTransmittal;
    selectedPackage: PaymentPackage;
}

@Component({
    selector: 'payment-packages-table',
    templateUrl: 'payment-packages-table.component.html',
    standalone: false,
})
export class PaymentPackagesTableComponent implements OnInit {
    @Input() paymentPackages: PaymentPackage[];
    @Input() readOnly: boolean = true;
    @Input() isModal: boolean = false;
    @Input() isPrelimApFeed: boolean;
    @Input() isFromPaymentBatch: boolean = false;
    @Output() showDetails: EventEmitter<ShowDetailOutput> = new EventEmitter<ShowDetailOutput>();
    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() showSaveButton: EventEmitter<boolean> = new EventEmitter<boolean>();

    constructor(
        private readonly _transmittalService: TransmittalService,
        private readonly messageBox: MessageBoxService,
        private readonly toastr: ToastrService,
        private readonly transmittalService: TransmittalService,
        private readonly _weissmanModalService: WeissmanModalService,
    ) {}

    selectedCount: number = 0;
    isDisabled: boolean = false;
    hoveredRecipient: PaymentPackageRecipient;

    RecipientType = RecipientType;

    ngOnInit() {
        if (this.isFromPaymentBatch) {
            this.toggleSelectAll();
            this.isDisabled = true;
        }
    }

    get shouldShowSelectAll() {
        return some(this.paymentPackages, (x) => !x.completedDateTime);
    }

    selectRow(event: any, packageItem: PaymentPackage): void {
        if (!this.isFromPaymentBatch && !packageItem.completedDateTime && !/no-bubble/.test(event.target.className)) {
            packageItem.isSelected = !packageItem.isSelected;
            this.selectedCount = filter(this.paymentPackages, (item) => item.isSelected).length;

            this.showSaveButton.emit(this.selectedCount !== 0);
        }
    }

    toggleSelectAll(): void {
        const updateValue = this.selectedCount !== this.paymentPackages.length;

        this.paymentPackages = map(this.paymentPackages, (paymentItem: PaymentPackage) => {
            paymentItem.isSelected = updateValue && !paymentItem.completedDateTime;
            return paymentItem;
        });

        this.selectedCount = filter(this.paymentPackages, (item) => item.isSelected).length;
        this.showSaveButton.emit(this.selectedCount !== 0);
    }

    openReviewModal(event: any, packageItem: PaymentPackage): void {
        this._transmittalService.showPaymentPackageReviewModal(packageItem);
    }

    unlockPackage(event: any, packageItem: PaymentPackage) {
        this.messageBox
            .confirm('Unlocking this package will undo your changes to the package email. Are you sure?')
            .then(() => {
                packageItem.emailSubjectTemplate = packageItem.originalEmailSubjectTemplate;
                packageItem.emailTemplate = packageItem.originalEmailTextTemplate;
                packageItem.lockedForOverrideEmail = false;
            });
    }

    setHoveredRecipient(recipient: PaymentPackageRecipient): void {
        this.hoveredRecipient = recipient;
    }

    getRecipientCount(recipientList: PaymentPackageRecipient[]): number {
        return filter(
            recipientList,
            (recipient) =>
                recipient.recipientType === RecipientType.Bcc || recipient.recipientType === RecipientType.Cc,
        ).length;
    }

    removeRecipient(event: any, recipient: PaymentPackageRecipient, packageItem: PaymentPackage): void {
        packageItem.paymentPackageRecipients = reject(
            packageItem.paymentPackageRecipients,
            (recipientItem: PaymentPackageRecipient) => {
                return recipientItem.contactID === recipient.contactID;
            },
        );
    }

    async openContactModal(event: any, packageItem: PaymentPackage): Promise<void> {
        const params = {
            recipientMode: true,
            entityInfo: {
                id: packageItem.paymentPackageID,
                type: EntityTypeIds.PAYMENTPACKAGE,
            },
        } as ContactListModalParams;

        const recipientObject = await this._weissmanModalService.showAsync(
            ContactListModalComponent,
            params,
            'modal-lg',
        );

        if (recipientObject) {
            forEach(recipientObject.recipients, (recipient: any) => {
                if (!recipient.email) {
                    this.toastr.error(
                        `Contact ${recipient.firstName} ${recipient.lastName} is missing email address!`,
                        'Missing email address',
                    );
                    return;
                }

                let companyName: string = '';
                if (recipient.companyName) {
                    companyName = recipientObject.companyName;
                } else if (recipient.entityName) {
                    companyName = recipient.entityName;
                }

                const tempRecipient: PaymentPackageRecipient = new PaymentPackageRecipient(
                    recipientObject.recipientType,
                    recipient.contactID,
                    `${recipient.firstName} ${recipient.lastName}`,
                    recipient.title,
                    recipient.address,
                    recipient.email,
                    companyName,
                );

                packageItem.paymentPackageRecipients.push(tempRecipient);
            });
        }
    }

    goToDetails(selectedTransmittal: PaymentTransmittal, selectedPackage: PaymentPackage): void {
        this.showDetails.emit({ selectedTransmittal, selectedPackage });
    }

    removeTransmittal(event: any, packageItem: PaymentPackage, transmittalIndex: number): void {
        if (packageItem.paymentTransmittals.length === 1) {
            this.paymentPackages = reject(this.paymentPackages, (item: PaymentPackage) => {
                return item.paymentPackageID === packageItem.paymentPackageID;
            });
        } else {
            packageItem.paymentTransmittals.splice(transmittalIndex, 1);
        }
    }

    recallPackage(packageItem: PaymentPackage): void {
        this.messageBox.confirm(`Are you sure you want to recall package #${packageItem.packageNumber}?`).then(() => {
            this.loading.emit(true);
            this.transmittalService
                .RecallPaymentPackage(packageItem)
                .then(() => {
                    // Remove the recalled package from the list, set it's "isRecalled" flag, and add it back to the end
                    const removedPackages: PaymentPackage[] = remove(this.paymentPackages, {
                        paymentPackageID: packageItem.paymentPackageID,
                    });

                    removedPackages.forEach((paymentPackage) => {
                        paymentPackage.isRecalled = true;
                        this.paymentPackages.push(paymentPackage);
                    });

                    this.paymentPackages = sortBy(this.paymentPackages, (x: PaymentPackage) => {
                        return parseInt(x.packageNumber);
                    });
                })
                .finally(() => {
                    this.loading.emit(false);
                });
        });
    }
}
